import React, { useState, useEffect, useCallback, useRef } from "react";
import { ApiService } from "../../service/api.service";
import { useNavigate } from "react-router-dom";

// Define the column configuration type
export interface ColumnConfig {
  key: string;
  header: string;
  visible: boolean;
  formatter?: (value: any, row: any) => React.ReactNode;
}

// Define the item type for the dropdown
export interface DropdownItem {
  id: string | number;
  [key: string]: any;
}

// Props for the DynamicModalDropDown component
export interface DynamicModalDropDownProps {
  label: string;
  name: string;
  endpoint: string;
  register: any;
  errors: any;
  disabled?: boolean;
  defaultValue?: string | number;
  setValue?: any;
  onSelect?: (item: DropdownItem) => void;
  columns: ColumnConfig[];
  idField: string;
  labelField: string | ((item: any) => string);
  searchFields?: string[];
  addNewPath?: string;
  addNewLabel?: string;
  findByIdEndpoint?: string;
  entityIdField?: string;
  operationAPI?: string;
  extraFetch?: any;
  extraFetchProp?: string;
}

const DynamicModalDropDown: React.FC<DynamicModalDropDownProps> = ({
  label,
  name,
  endpoint,
  register,
  errors,
  disabled = false,
  defaultValue = "",
  setValue,
  onSelect,
  columns,
  idField,
  labelField,
  searchFields = [],
  addNewPath,
  addNewLabel = "Add new",
  findByIdEndpoint,
  entityIdField = "entityId",
  operationAPI,
  extraFetch,
  extraFetchProp
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<DropdownItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<DropdownItem | null>(null);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  // Flag to track if default value has been processed
  const defaultValueProcessedRef = useRef(false);

  const apiService = new ApiService();
  const entityId = localStorage.getItem("entity_id");
  const navigate = useNavigate();

  // Use a ref to track the current request so we can cancel it
  const currentRequestRef = useRef<AbortController | null>(null);

  // Add overflow hidden to body when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Only fetch options when dropdown is opened and not fetched before
  useEffect(() => {
    if (open && !initialFetchDone) {
      fetchOptions();
      setInitialFetchDone(true);
    }
  }, [open]);

  // Get formatted label from item
  // Helper function to get nested property values
  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  const getItemLabel = (item: any): string => {
    if (typeof labelField === 'function') {
      return labelField(item);
    }
    return getNestedValue(item, labelField) || "Unknown";
  };

  const getItemId = (item: any): any => {
    return getNestedValue(item, idField);
  };

  // Debounce function to reduce API calls
  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      if (open) {
        fetchOptions(searchTerm);
      }
    }, 500),
    [endpoint, entityId, open]
  );

  // Handle search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  // Fetch options from API with proper cancellation
  const fetchOptions = async (searchTerm: string = "") => {
    // Cancel any ongoing request
    if (currentRequestRef.current) {
      currentRequestRef.current.abort();
    }

    // Create a new abort controller for this request
    const abortController = new AbortController();
    currentRequestRef.current = abortController;

    try {
      setLoading(true);
      setError(null);

      // Construct search params
      const searchParams = new URLSearchParams();
      searchParams.append("status", "1");

      if (entityId) {
        searchParams.append(entityIdField, entityId);
      }

      if (searchTerm && searchFields.length > 0) {
        searchParams.append("search", searchTerm);
      }

      const response: any = await apiService.getApi(
        `${operationAPI}/${endpoint}?${searchParams.toString()}`,
        abortController.signal
      );

      // Only process the response if this request wasn't canceled
      if (!abortController.signal.aborted) {
        if (response?.body?.data && Array.isArray(response.body.data)) {
          setOptions(response.body.data);
        } else {
          setOptions([]);
        }
      }
    } catch (err: any) {
      // Only set error if this request wasn't canceled
      if (err.name !== "CanceledError" && err.name !== "AbortError") {
        console.error("API fetch error:", err);
        setError(`Failed to fetch ${label || "items"}`);
        setOptions([]);
      }
    } finally {
      if (currentRequestRef.current === abortController) {
        setLoading(false);
        currentRequestRef.current = null;
      }
    }
  };

  // Fetch item by ID using the endpoint
  const fetchItemById = async (value: string | number) => {
    if (!value) return;

    try {
      setLoading(true);
      console.log(`Fetching item by ID: ${value}`); // Debug log

      // Use custom findByIdEndpoint if provided, otherwise construct from main endpoint
      const lookupEndpoint = findByIdEndpoint || `${operationAPI}/${endpoint}`;

      // Create URL with appropriate parameters
      const url = new URL(lookupEndpoint, window.location.origin);
      url.searchParams.append("status", "1");

      if (entityId) {
        url.searchParams.append(entityIdField, entityId);
      }

      // Add ID parameter - this might need to be customized based on API
      url.searchParams.append(idField, value.toString());

      console.log(`API URL: ${url.toString()}`); // Debug log
      const response: any = await apiService.getApi(url.toString());
      console.log(`API Response:`, response); // Debug log

      // Handle different response structures
      if (response?.body) {
        let foundItem: any = null;

        // Case 1: Direct object response
        if (!Array.isArray(response.body) && !response.body.data && typeof response.body === 'object') {
          foundItem = response.body;
        }
        // Case 2: Data array with items
        else if (response.body.data && Array.isArray(response.body.data) && response.body.data.length > 0) {
          foundItem = response.body.data[0];
        }
        // Case 3: Direct array response
        else if (Array.isArray(response.body) && response.body.length > 0) {
          foundItem = response.body[0];
        }

        console.log(`Found item:`, foundItem); // Debug log

        if (foundItem) {
          // Flatten the data structure if needed for the component
          const flattenedItem = {
            ...foundItem,
            // If the item has nested bookingDetail, flatten the common fields
            ...(foundItem.bookingDetail ? {
              bookingId: foundItem.bookingDetail.bookingId,
              bookingName: foundItem.bookingDetail.bookingName,
              bookingNumber: foundItem.bookingDetail.bookingNumber,
              startDate: foundItem.bookingDetail.startDate,
              endDate: foundItem.bookingDetail.endDate,
              bookingType: foundItem.bookingDetail.bookingType,
              status: foundItem.bookingDetail.status,
              isActive: foundItem.bookingDetail.isActive,
              bookingCreatedBy: foundItem.bookingDetail.bookingCreatedBy
            } : {})
          };

          setSelected(flattenedItem);
          console.log(`Set selected item:`, flattenedItem); // Debug log
          //   console.log("Selected item:", foundItem);
          extraFetch && extraFetchProp && extraFetch(flattenedItem[extraFetchProp]);
          // Call onSelect callback if provided
          if (onSelect) {
            onSelect(flattenedItem);
          }
        } else {
          console.warn(`No item found for ID: ${value}`);
          // Set a fallback selected item to prevent controlled/uncontrolled issues
          const fallbackItem: DropdownItem = {
            id: value,
            [idField]: value,
            label: `Item ${value}` // Fallback label
          };
          if (typeof labelField === 'string') {
            fallbackItem[labelField] = `Item ${value}`;
          }
          setSelected(fallbackItem);
        }
      } else {
        console.warn(`No response body for ID: ${value}`);
        // Set a fallback selected item to prevent controlled/uncontrolled issues
        const fallbackItem: DropdownItem = {
          id: value,
          [idField]: value,
          label: `Item ${value}` // Fallback label
        };
        if (typeof labelField === 'string') {
          fallbackItem[labelField] = `Item ${value}`;
        }
        setSelected(fallbackItem);
      }
    } catch (err) {
      console.error(`Failed to fetch selected ${label || "item"} data:`, err);
      // Set a fallback selected item to prevent controlled/uncontrolled issues
      const fallbackItem: DropdownItem = {
        id: value,
        [idField]: value,
        label: `Item ${value}` // Fallback label
      };
      if (typeof labelField === 'string') {
        fallbackItem[labelField] = `Item ${value}`;
      }
      setSelected(fallbackItem);
    } finally {
      setLoading(false);
    }
  };

  // Set default value when component initializes or when it changes
  useEffect(() => {
    if (defaultValue && !defaultValueProcessedRef.current) {
      defaultValueProcessedRef.current = true;
      console.log(`Processing defaultValue: ${defaultValue}`); // Debug log
      fetchItemById(defaultValue);
    } else if (defaultValue && selected && defaultValue !== getItemId(selected)) {
      // Reset if default value changes
      console.log(`Default value changed from ${getItemId(selected)} to ${defaultValue}`); // Debug log
      defaultValueProcessedRef.current = false;
      setSelected(null);
      fetchItemById(defaultValue);
    } else if (!defaultValue && selected) {
      // Clear selection if defaultValue is removed
      console.log(`Clearing selection as defaultValue is now empty`); // Debug log
      setSelected(null);
    }
  }, [defaultValue]);

  // Clean up any pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (currentRequestRef.current) {
        currentRequestRef.current.abort();
      }
    };
  }, []);

  // Handle selection and send value to form
  const handleSelect = (item: DropdownItem) => {
    setSelected(item);
    setOpen(false);

    if (setValue) {
      setValue(name, item[idField], { shouldValidate: true, shouldDirty: true });
    }

    // Call onSelect callback if provided
    if (onSelect) {
      onSelect(item);
    }
  };

  // Filter options by search term if client-side filtering is needed
  const filteredOptions = options.filter((option) => {
    // If no search or no searchFields defined, return all options
    if (!search || searchFields.length === 0) return true;

    // Check if any of the search fields contain the search term
    return searchFields.some(field => {
      const value = option[field];
      return value && String(value).toLowerCase().includes(search.toLowerCase());
    });
  });

  // Register form input
  const { ref, ...rest } = register(name);

  // Close modal when clicking outside or pressing ESC
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  return (
    <div className="w-full">
      <label htmlFor={name} className="input-label block text-sm font-medium text-gray-700 mb-1">
        {label ? label : <>&nbsp;</>}
      </label>

      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setOpen(true)}
          className={`w-full px-4 py-2 border rounded-lg font-medium bg-white text-left text-[14px]
            focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all
            ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
            ${errors[name] ? "border-red-500" : "border-gray-300"}
          `}
        >
          {selected && getItemId(selected) ? getItemLabel(selected) : (
            <span className="text-gray-400">{`Select ${label || "item"}`}</span>
          )}
        </button>

        {/* Hidden input to hold the form value */}
        <input
          {...rest}
          ref={ref}
          type="hidden"
          value={selected && getItemId(selected) ? getItemId(selected) : ""}
          id={name}
          name={name}
        />

        {errors[name] && (
          <p className="text-red-500 text-xs mt-1">{errors[name]?.message}</p>
        )}
      </div>

      {/* Modal with full screen overlay */}
      {open && (
        <>
          {/* Fixed position overlay that covers the entire viewport with higher z-index */}
          <div
            className="fixed inset-0 bg-black bg-opacity-75 z-[10001]"
            onClick={() => setOpen(false)}
          ></div>

          {/* Modal content with higher z-index than the sidebar */}
          <div
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl w-[95%] max-w-4xl p-4 md:p-6 animate-fadeIn z-[10002]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">Select {label || "item"}</h2>
              <div className="gap-2 md:gap-4 flex items-center">
                {addNewPath && (
                  <button
                    type="button"
                    onClick={() => navigate(addNewPath)}
                    className="text-sm md:text-base text-[#444FBD] font-[600] hover:underline"
                  >
                    {addNewLabel}
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <input
              type="text"
              placeholder={`Search ${label || "items"}...`}
              value={search}
              onChange={handleSearchChange}
              className="w-full px-4 py-2 border rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />

            {/* Loading and Error States */}
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : error ? (
              <div className="text-center text-red-500 py-4">
                {error}
                <button onClick={() => fetchOptions(search)} className="text-blue-500 underline ml-2">
                  Retry
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="overflow-y-auto max-h-[50vh]">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 text-gray-700 sticky top-0">
                      <tr>
                        {columns.filter(col => col.visible).map(column => (
                          <th
                            key={column.key}
                            className={`px-2 md:px-4 py-2 text-left ${column.key !== "actions" ? "" : "text-center"
                              }`}
                          >
                            {column.header}
                          </th>
                        ))}
                        <th className="px-2 md:px-4 py-2 text-center">Select</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOptions.length > 0 ? (
                        filteredOptions.map((item) => (
                          <tr
                            key={item[idField]?.toString()}
                            className="hover:bg-gray-50 transition cursor-pointer border-b border-gray-100"
                            onClick={() => handleSelect(item)}
                          >
                            {columns.filter(col => col.visible).map(column => (
                              <td key={`${item[idField]}-${column.key}`} className="px-2 md:px-4 py-2">
                                {column.formatter
                                  ? column.formatter(item[column.key], item)
                                  : item[column.key] || "N/A"}
                              </td>
                            ))}
                            <td className="px-2 md:px-4 py-2 text-center">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelect(item);
                                }}
                                className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600 transition text-xs md:text-sm"
                              >
                                Select
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={columns.filter(col => col.visible).length + 1}
                            className="px-4 py-4 text-center text-gray-500"
                          >
                            No {label?.toLowerCase() || "items"} found. Try adjusting your search.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DynamicModalDropDown;